import type { CustomFieldSchema, CustomFieldValue } from '@/types/custom-fields';

export function dateStringToDate(value: string): Date | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

export function dateToDateString(value: Date | undefined): string {
  if (!value) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getCustomFieldOptions(schema: CustomFieldSchema) {
  return (schema.definition.config as { options?: { key: string; label: string }[] } | undefined)
    ?.options;
}

export function parseMultiSelectDraftValue(value: string): string[] {
  if (!value.trim()) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    if (Array.isArray(parsed)) return parsed.map(String);
  } catch {
    // Fall back to the legacy single-value draft format.
  }
  return [value];
}

export function serializeMultiSelectDraftValue(values: string[]): string {
  return JSON.stringify(values);
}

export function getInitialCustomFieldDraftValue(schema: CustomFieldSchema): string {
  return schema.definition.fieldType === 'CHECKBOX' ? 'false' : '';
}

export function customFieldValueToDraftString(schema: CustomFieldSchema, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (Array.isArray(value)) return serializeMultiSelectDraftValue(value.map(String));
  if (value instanceof Date) return dateToDateString(value);
  if (schema.definition.fieldType === 'CURRENCY' && typeof value === 'number') {
    return String(value / 100);
  }
  const str = String(value);
  // Only DATE carries an ISO time part ("…T…") to strip; splitting on 'T' for any
  // text corrupts values with a literal "T" (e.g. "QA-TEST-9999" → "QA-").
  if (schema.definition.fieldType === 'DATE') return str.split('T')[0] ?? str;
  return str;
}

export function customFieldDraftToPayloadValue(schema: CustomFieldSchema, value: string): unknown {
  const trimmed = value.trim();
  if (schema.definition.fieldType === 'CHECKBOX') return trimmed === 'true';
  if (trimmed === '') return null;
  if (schema.definition.fieldType === 'NUMBER') return Number(trimmed);
  if (schema.definition.fieldType === 'CURRENCY') return Math.round(Number(trimmed) * 100);
  if (schema.definition.fieldType === 'MULTI_SELECT') return parseMultiSelectDraftValue(trimmed);
  return trimmed;
}

export function formatCustomFieldDisplayValue(schema: CustomFieldSchema, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (schema.definition.fieldType === 'CHECKBOX') return value === true ? 'Yes' : 'No';
  if (schema.definition.fieldType === 'CURRENCY') {
    const cents = Number(value);
    if (!Number.isFinite(cents)) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
      cents / 100,
    );
  }
  if (schema.definition.fieldType === 'DATE' && typeof value === 'string') {
    const parsed = new Date(`${value.split('T')[0]}T00:00:00.000Z`);
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(parsed);
  }

  const options = getCustomFieldOptions(schema);
  if (Array.isArray(value)) {
    return value
      .map((item) => options?.find((option) => option.key === item)?.label ?? String(item))
      .join(', ');
  }
  if (options?.length) {
    return options.find((option) => option.key === value)?.label ?? String(value);
  }
  return String(value);
}

export function formatCustomFieldValueForPdf(
  fieldType: string,
  value: unknown,
  config: Record<string, unknown>,
): string {
  if (value === null || value === undefined || value === '') return '';
  if (fieldType === 'CHECKBOX') return value === true ? 'Yes' : 'No';
  if (fieldType === 'CURRENCY') {
    const cents = Number(value);
    if (!Number.isFinite(cents)) return '';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
      cents / 100,
    );
  }
  if (fieldType === 'DATE' && typeof value === 'string') {
    const parsed = new Date(`${value.split('T')[0]}T00:00:00.000Z`);
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(parsed);
  }
  const options = (config as { options?: { key: string; label: string }[] }).options;
  if (Array.isArray(value)) {
    return value
      .map((item) => options?.find((opt) => opt.key === item)?.label ?? String(item))
      .join(', ');
  }
  if (options?.length) {
    return options.find((opt) => opt.key === value)?.label ?? String(value);
  }
  return String(value);
}

export function getDefaultCustomFieldKeys(schemas: CustomFieldSchema[], mode: 'create' | 'edit') {
  return schemas
    .filter((schema) => {
      if (schema.association.isRequired) return true;
      if (!schema.association.isDefault) return false;
      return mode === 'edit' || schema.association.showOnCreate;
    })
    .map((schema) => schema.definition.fieldKey);
}

export function deriveCustomFieldDraftState(
  existingValues: CustomFieldValue[] | null | undefined,
  schemas: CustomFieldSchema[],
  mode: 'create' | 'edit',
): { customFields: Record<string, string>; activeFieldKeys: string[] } {
  const customFields: Record<string, string> = {};
  const activeFieldKeys: string[] = [];

  for (const value of existingValues ?? []) {
    const schema = schemas.find((item) => item.definition.id === value.fieldDefinitionId);
    if (!schema || value.value === null || value.value === undefined) continue;
    customFields[schema.definition.fieldKey] = customFieldValueToDraftString(schema, value.value);
    activeFieldKeys.push(schema.definition.fieldKey);
  }

  for (const fieldKey of getDefaultCustomFieldKeys(schemas, mode)) {
    if (!activeFieldKeys.includes(fieldKey)) {
      activeFieldKeys.push(fieldKey);
    }
    const schema = schemas.find((item) => item.definition.fieldKey === fieldKey);
    if (schema && customFields[fieldKey] === undefined) {
      customFields[fieldKey] = getInitialCustomFieldDraftValue(schema);
    }
  }

  return { customFields, activeFieldKeys };
}

/**
 * COPY_ON_CREATE inheritance: resolve the draft values a target entity should be
 * pre-filled with from its source entities, when created. For each target field
 * carrying an active COPY_ON_CREATE rule from a source present in
 * `sourceValuesByEntityType`, take that source's value (same field definition id)
 * and return it as a draft string keyed by fieldKey. Sources without a value are
 * skipped (the field is simply left empty — no invented value). ALWAYS_SYNC is
 * deprecated, so only COPY_ON_CREATE rules are honoured.
 */
export function resolveInheritedDraftValues(
  targetSchemas: CustomFieldSchema[],
  sourceValuesByEntityType: Partial<Record<string, CustomFieldValue[] | null | undefined>>,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const schema of targetSchemas) {
    for (const rule of schema.inheritanceRules ?? []) {
      if (rule.isActive === false) continue;
      if (rule.behavior !== 'COPY_ON_CREATE') continue;
      const sourceKey = rule.sourceEntityType?.toLowerCase();
      if (!sourceKey) continue;
      const sourceValues = sourceValuesByEntityType[sourceKey];
      if (!sourceValues) continue;
      const match = sourceValues.find((v) => v.fieldDefinitionId === schema.definition.id);
      if (!match || match.value === null || match.value === undefined) continue;
      result[schema.definition.fieldKey] = customFieldValueToDraftString(schema, match.value);
      break; // first source (by rule order) that actually has a value wins
    }
  }
  return result;
}

export function buildCustomFieldValues(
  values: Record<string, string>,
  schemas: CustomFieldSchema[],
  clearedFieldKeys: string[] = [],
  activeFieldKeys?: string[],
): { fieldDefinitionId: string; value: unknown }[] {
  const keyToSchema = new Map(schemas.map((schema) => [schema.definition.fieldKey, schema]));
  const payload: { fieldDefinitionId: string; value: unknown }[] = [];
  const fieldKeys = activeFieldKeys ?? Object.keys(values);

  for (const fieldKey of fieldKeys) {
    const schema = keyToSchema.get(fieldKey);
    if (!schema) continue;
    const value = values[fieldKey] ?? getInitialCustomFieldDraftValue(schema);
    if (
      value === '' &&
      schema.definition.fieldType !== 'CHECKBOX' &&
      activeFieldKeys === undefined
    ) {
      continue;
    }
    payload.push({
      fieldDefinitionId: schema.definition.id,
      value: customFieldDraftToPayloadValue(schema, value),
    });
  }

  for (const fieldKey of clearedFieldKeys) {
    const schema = keyToSchema.get(fieldKey);
    if (!schema) continue;
    payload.push({ fieldDefinitionId: schema.definition.id, value: null });
  }

  return payload;
}
